import React from "react";
import { formatDateTime } from "src/utils/date";
import { formatCentsToDollarsWithDollarSign } from "src/helpers";
import { OrderItem } from "src/interfaces/order.interface";
import { CustomerInfoSectionProps } from "../../../../interfaces/order-management-props.interface";

const displayName = (first?: string | null, last?: string | null) => {
  const f = first?.trim();
  const l = last?.trim();
  const parts = [f, l].filter(Boolean);
  return parts.length ? parts.join(" ") : "—";
};

const CustomerInfoSection = ({
  customerOrder,
  itemsTotalPrice,
  itemTotalTax,
  discountAmountCents,
  promotionCode,
  finalTotal,
}: CustomerInfoSectionProps) => {
  const initials = `${customerOrder?.customerFirstName?.charAt(0) ?? ""}${customerOrder?.customerLastName?.charAt(0) ?? ""}`.toUpperCase();

  return (
    <div className="mt-0">
      <h2 className="text-sm font-semibold text-[#111827]">Customer</h2>

      <div className="mt-2 flex flex-wrap items-start justify-between gap-2">
        <div className="flex min-w-0 items-center gap-x-2">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-[#FD8064]">
            <span className="text-sm font-medium text-white">{initials || "?"}</span>
          </span>
          <div className="min-w-0">
            <p className="truncate text-sm font-medium text-[#111827]">
              {displayName(customerOrder?.customerFirstName, customerOrder?.customerLastName)}
            </p>
            <p className="text-xs text-[#666666]">Order #{customerOrder?.correlationId}</p>
          </div>
        </div>
        <p className="shrink-0 text-xs text-[#666666]">
          {formatDateTime(customerOrder?.createdAt)}
        </p>
      </div>

      <h3 className="mb-2 mt-5 text-sm font-semibold text-[#111827]">Items</h3>
      <div className="max-h-[min(50vh,420px)] overflow-y-auto overscroll-contain rounded-lg border border-[#e5e7eb] bg-white">
        <table className="w-full text-[13px]">
          <thead className="sticky top-0 z-[1] bg-[#f9fafb]">
            <tr className="border-b border-[#e5e7eb] text-[#6B7280]">
              <th className="px-2 py-1.5 text-left font-medium">Name</th>
              <th className="w-12 px-2 py-1.5 text-center font-medium">Qty</th>
              <th className="w-16 px-2 py-1.5 text-right font-medium">Price</th>
              <th className="w-20 px-2 py-1.5 text-right font-medium">Subtotal</th>
            </tr>
          </thead>
          <tbody>
            {customerOrder?.items?.map((orderItem: OrderItem, index: number) => {
              const unitCents = orderItem?.unitPriceCents ?? orderItem?.priceCents ?? 0;
              const qty = orderItem?.quantity ?? 0;
              const subtotalCents =
                orderItem?.lineSubtotalCents != null
                  ? orderItem.lineSubtotalCents
                  : unitCents * qty;
              return (
                <tr key={orderItem?.correlationId ?? `item-${index}`} className="border-b border-[#f3f4f6]">
                  <td className="px-2 py-1.5 text-[#111827]">{orderItem?.englishName ?? "—"}</td>
                  <td className="px-2 py-1.5 text-center text-[#111827]">{qty}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums text-[#111827]">
                    {formatCentsToDollarsWithDollarSign(unitCents)}
                  </td>
                  <td className="px-2 py-1.5 text-right font-medium tabular-nums text-[#111827]">
                    {formatCentsToDollarsWithDollarSign(subtotalCents)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <div className="border-t border-[#e5e7eb] text-[13px]">
          <div className="flex justify-between bg-[#f9fafb] px-2 py-1.5">
            <span className="text-[#6B7280]">
              {customerOrder?.items?.length === 1
                ? `Item (${customerOrder?.items?.length ?? 0})`
                : `Items (${customerOrder?.items?.length ?? 0})`}
            </span>
            <span className="font-medium tabular-nums text-[#111827]">
              {formatCentsToDollarsWithDollarSign(itemsTotalPrice)}
            </span>
          </div>

          {discountAmountCents > 0 && (
            <div className="flex justify-between bg-[#f9fafb] px-2 py-1.5">
              <span className="text-[#6B7280]">
                Discount{promotionCode ? ` (${promotionCode})` : ""}
              </span>
              <span className="tabular-nums text-[#6B7280]">
                −{formatCentsToDollarsWithDollarSign(discountAmountCents)}
              </span>
            </div>
          )}

          <div className="flex justify-between bg-[#f9fafb] px-2 py-1.5">
            <span className="text-[#6B7280]">Estimated tax</span>
            <span className="font-medium tabular-nums text-[#111827]">
              {formatCentsToDollarsWithDollarSign(
                itemTotalTax
              )}
            </span>
          </div>

          {customerOrder?.cardProcessingFeeCents != null && customerOrder.cardProcessingFeeCents > 0 && (
            <div className="flex justify-between bg-[#f9fafb] px-2 py-1.5">
              <span className="text-[#6B7280]">Card processing fee</span>
              <span className="font-medium tabular-nums text-[#111827]">
                {formatCentsToDollarsWithDollarSign(customerOrder.cardProcessingFeeCents)}
              </span>
            </div>
          )}

          <div className="flex justify-between bg-[#f3f4f6] px-2 py-2 text-[#111827]">
            <span className="text-base font-bold">Total</span>
            <span className="text-base font-bold tabular-nums">
              {formatCentsToDollarsWithDollarSign(finalTotal)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerInfoSection;
